import React, { useEffect, useState } from 'react';
import CustomerForm from './components/CustomerForm';
import './index.css';
import axios from 'axios';
import SubmissionMessage from './components/SubmissionMessage';
import { hexToHSL } from './utils/hexToHsl';
import formatPhone from './utils/formatPhoneForUser';

const toSixDigitHex = (color) => {
	const hex = String(color || '').trim().replace('#', '');
	const fullHex =
		hex.length === 3
			? hex
					.split('')
					.map((char) => char + char)
					.join('')
			: hex;
	return /^[0-9a-f]{6}$/i.test(fullHex) ? fullHex : '';
};

const getLuminance = (hex) => {
	const [r, g, b] = hex.match(/../g).map((pair) => {
		const value = parseInt(pair, 16) / 255;
		return value <= 0.03928
			? value / 12.92
			: Math.pow((value + 0.055) / 1.055, 2.4);
	});
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const darkenHex = (hex, amount = 0.3) =>
	hex
		.match(/../g)
		.map((pair) =>
			Math.round(parseInt(pair, 16) * (1 - amount))
				.toString(16)
				.padStart(2, '0')
		)
		.join('');

// Same text colour and dark shade MUI derived from the brand colour.
const updateBrandColor = (color) => {
	const hex = toSixDigitHex(color);
	if (!hex) return;

	const { style } = document.documentElement;
	style.setProperty('--brand', hexToHSL(hex));
	style.setProperty('--brand-dark', hexToHSL(darkenHex(hex)));
	style.setProperty(
		'--brand-foreground',
		1.05 / (getLuminance(hex) + 0.05) >= 3 ? '0 0% 100%' : '0 0% 13%'
	);
};

const App = () => {
	const errorColor = '#c0392b';
	const brandPromotionalMessage =
		'Please enter your information so we can text you updates and respond to you.';
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [brandColor, setBrandColor] = useState('#03B856'); // default color, will render until API request is successful
	const [businessName, setBusinessName] = useState('');
	const [apiError, setApiError] = useState(false);
	const [apiErrorMessage, setApiErrorMessage] = useState('');
	const [_business, setBusiness] = useState('');
	const [loading, setLoading] = useState(false);
	const [isIncludingPhone, setIsIncludingPhone] = useState(true);
	const [isIncludingEmail, setIsIncludingEmail] = useState(false);
	const [termsOfServiceUrl, setTermsOfServiceUrl] = useState('');
	const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
	const [supportOptIn, setSupportOptIn] = useState(false);
	const [marketingOptIn, setMarketingOptIn] = useState(false);
	const [termsAccepted, setTermsAccepted] = useState(false);

	//set controlled input states
	const [name, setName] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState(false);
	const [nameError, setNameError] = useState(false);
	const [phoneNumberError, setPhoneNumberError] = useState(false);

	const url = window.location.href;
	const splitUrl = url.split('/');
	// Support URLs with or without trailing slash.
	const twilioNumber =
		splitUrl[splitUrl.length - 1] || splitUrl[splitUrl.length - 2] || '';

	//updates the key-value pair for the input field that triggered the event
	const handleNameChange = (event) => {
		const value = event.target.value;
		setName(value);
	};

	const handlePhoneChange = (event) => {
		const value = event.target.value;
		const formatted = formatPhone(value);
		setPhoneNumber(formatted);
	};

	const handleEmailChange = (event) => {
		const value = event.target.value;
		setEmail(value);
	};

	useEffect(() => {
		updateBrandColor(brandColor);
	}, [brandColor]);

	// Populate states
	useEffect(() => {
		(async function fetchBusinessData() {
			try {
				const response = await axios.post(
					'/api/get-business-information',
					{
						twilioNumber,
					}
				);
				if (response.data && response.status === 200) {
					setBrandColor(response.data.brandColor);
					setBusinessName(response.data.businessName);
					setBusiness(response.data._business);
					document.title = response.data.businessName;
					setTermsOfServiceUrl(response.data.termsOfServiceUrl || '');
					setPrivacyPolicyUrl(response.data.privacyPolicyUrl || '');

					if (response.data._business) {
						const formDataResponse = await axios.post(
							'/api/get-opt-in-form-options/',
							{
								_business: response.data._business,
							}
						);

						if (
							formDataResponse.data &&
							!formDataResponse.data.error
						) {
							const { data } = formDataResponse;

							setIsIncludingEmail(
								data.optInFormOptions.isIncludingEmail
							);
							setIsIncludingPhone(
								data.optInFormOptions.isIncludingPhone
							);
						}
					}
				}
			} catch (e) {
				setApiError(true);
				setApiErrorMessage(e.response.data.message);
				setBrandColor(errorColor);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const resetInputs = () => {
		setName('');
		setPhoneNumber('');
		setEmail('');
		setLoading(false);
		setPhoneNumberError(false);
		setNameError(false);
		setEmailError(false);
		setSupportOptIn(false);
		setMarketingOptIn(false);
		setTermsAccepted(false);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		// Non-intrusive: keep user on form; show inline error if not opted in.
		// Must accept terms AND opt in to at least one (support or marketing)
		if (!termsAccepted || (!supportOptIn && !marketingOptIn)) {
			// Reset fields so when we return, the form is blank.
			resetInputs();

			// Show the lightweight "not opted in" screen briefly (like before).
			setApiError(true);
			setApiErrorMessage('You have not opted in to SMS messages.');
			setIsSubmitted(true);

			setTimeout(() => {
				setIsSubmitted(false);
			}, '5000');
			return;
		}

		setLoading(true);

		try {
			const response = await axios.post('/api/submit-form', {
				name,
				phoneNumber,
				email,
				_business,
				businessName,
				isIncludingEmail,
				isIncludingPhone,
				supportOptIn,
				marketingOptIn,
				termsAccepted,
			});

			if (response) {
				resetInputs();
				setIsSubmitted(true);
				setApiError(false);

				setTimeout(() => {
					setIsSubmitted(false);
				}, '5000');
			}
		} catch (e) {
			// Reset fields so when we return, the form is blank.
			resetInputs();

			// Show the "not opted in" screen for any validation error
			setApiError(true);
			setApiErrorMessage('You have not opted in to SMS messages.');
			setIsSubmitted(true);

			setTimeout(() => {
				setIsSubmitted(false);
			}, '5000');

			setLoading(false);
		}
	};

	return (
		<div className="flex min-h-dvh w-full border-l-[0.625rem] border-brand bg-background font-sans text-foreground">
			<main className="mx-auto w-full max-w-[600px] px-4 py-3 sm:px-6 sm:py-6 md:py-10">
				{/*once the form is completed and values were submitted successfully the component will re-render as a success message.*/}
				{isSubmitted ? (
					<SubmissionMessage
						backgroundColor={brandColor}
						twilioNumber={twilioNumber}
						apiError={apiError}
						apiErrorMessage={apiErrorMessage}
						errorColor={errorColor}
						businessName={businessName}
						setIsSubmitted={setIsSubmitted}
						resetInputs={resetInputs}
					/>
				) : (
					<CustomerForm
						brandPromotionalMessage={brandPromotionalMessage}
						supportOptIn={supportOptIn}
						setSupportOptIn={setSupportOptIn}
						marketingOptIn={marketingOptIn}
						setMarketingOptIn={setMarketingOptIn}
						termsAccepted={termsAccepted}
						setTermsAccepted={setTermsAccepted}
						handleNameChange={handleNameChange}
						handlePhoneChange={handlePhoneChange}
						handleEmailChange={handleEmailChange}
						nameError={nameError}
						phoneNumberError={phoneNumberError}
						emailError={emailError}
						name={name}
						email={email}
						phoneNumber={phoneNumber}
						businessName={businessName}
						twilioNumber={twilioNumber}
						handleSubmit={handleSubmit}
						apiError={apiError}
						apiErrorMessage={apiErrorMessage}
						errorColor={errorColor}
						loading={loading}
						isIncludingPhone={isIncludingPhone}
						isIncludingEmail={isIncludingEmail}
						termsOfServiceUrl={termsOfServiceUrl}
						privacyPolicyUrl={privacyPolicyUrl}
					/>
				)}
			</main>
		</div>
	);
};

export default App;
